/**
 * ZIP-code autocomplete — inline table-cell combobox backed by the ZIP
 * geography endpoint (`/v2/zip-codes/?search=`). OR-881 / OR-1362.
 *
 * Behaviour (Henry, jul-22 — "a dropdown like when you type Facilities"):
 *   • The cell is a free-text ZIP input. Typing fires a debounced prefix
 *     search and shows matching ZIPs in a dropdown.
 *   • Picking a result auto-fills the row's city / state / county (FIPS) /
 *     geo (urbanicity) via `onSelect` — this is what fixes "county not
 *     populated".
 *   • Not picking keeps whatever was typed (free text).
 *
 * Mirrors `facility-name-autocomplete.tsx`: a hand-rolled, pointer-driven
 * dropdown portalled to <body> — the Schedule table's `EditableCellProvider`
 * swallows keydown (killing combobox keyboard nav) and `overflow:auto` would
 * clip an in-flow list. ZIP is kept as a string (leading zeros preserved).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "@tricura/atlas-ui";
import { searchZipCodes, type ZipCode } from "../api/endpoints/zip-codes";
import { useDebouncedValue } from "../hooks/use-debounced-value";

const CELL_SHELL =
    "relative flex h-9 w-full items-center rounded-lg border px-3 text-sm transition-colors hover:bg-secondary_hover focus-within:border-brand-primary";
const CELL_INPUT = "h-9 w-full bg-transparent text-primary outline-none placeholder:text-placeholder";

/** Don't hit the API until there's a meaningful ZIP prefix. */
const MIN_QUERY = 3;

/** "Houston, TX · Harris" — the option's differentiator line. */
function zipSubline(z: ZipCode): string {
    const place = [z.city, z.state].map((s) => s?.trim()).filter(Boolean).join(", ");
    return [place, z.county?.trim()].filter(Boolean).join(" · ");
}

interface ZipCodeAutocompleteProps {
    value: string;
    /** Free-text edits (every keystroke). */
    onChange: (zip: string) => void;
    /** A ZIP was picked — caller auto-fills the row (city/state/county/geo). */
    onSelect: (zip: ZipCode) => void;
    placeholder?: string;
    ariaLabel: string;
    isInvalid?: boolean;
    /** Custom attribute for E2E testing. */
    "data-autotest"?: string;
}

export function ZipCodeAutocomplete({ value, onChange, onSelect, placeholder, ariaLabel, isInvalid, "data-autotest": dataAutotest }: ZipCodeAutocompleteProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState<ZipCode[]>([]);
    const [loading, setLoading] = useState(false);
    const [rect, setRect] = useState<DOMRect | null>(null);
    /** Set right after a pick so the debounced effect doesn't re-search the
     *  value we just auto-filled (and reopen the dropdown). */
    const skipNextSearch = useRef(false);

    // Local buffer so typing paints instantly, decoupled from the deferred SoT
    // round-trip. Re-syncs only on genuine external changes (pick, save,
    // sibling), never on the lagging echo of our own emit.
    const lastEmitted = useRef(value);
    const [text, setText] = useState(value);
    useEffect(() => {
        if (value !== lastEmitted.current) {
            lastEmitted.current = value;
            setText(value);
        }
    }, [value]);

    const debounced = useDebouncedValue(text, 300);

    useEffect(() => {
        if (skipNextSearch.current) {
            skipNextSearch.current = false;
            return;
        }
        const q = debounced.trim();
        if (q.length < MIN_QUERY) {
            setResults([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        searchZipCodes(q, 8)
            .then((rows) => {
                if (cancelled) return;
                setResults(rows);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setResults([]);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debounced]);

    useEffect(() => {
        if (!open) return;
        const updateRect = () => {
            if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
        };
        updateRect();
        window.addEventListener("scroll", updateRect, true);
        window.addEventListener("resize", updateRect);
        return () => {
            window.removeEventListener("scroll", updateRect, true);
            window.removeEventListener("resize", updateRect);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: PointerEvent) => {
            const t = e.target as Node;
            if (inputRef.current?.contains(t) || listRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, [open]);

    const handleSelect = useCallback(
        (z: ZipCode) => {
            skipNextSearch.current = true;
            lastEmitted.current = z.zipCode;
            setText(z.zipCode);
            onSelect(z);
            setOpen(false);
            setResults([]);
            inputRef.current?.blur();
        },
        [onSelect],
    );

    const showList = open && rect && (loading || results.length > 0);

    return (
        <div className={cx(CELL_SHELL, isInvalid ? "border-error" : "border-transparent")}>
            <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                data-autotest={dataAutotest}
                aria-label={ariaLabel}
                value={text}
                placeholder={placeholder}
                className={CELL_INPUT}
                autoComplete="off"
                onFocus={() => setOpen(true)}
                onChange={(e) => {
                    // Digits only, max 5, kept as a STRING so leading zeros
                    // survive (OR-1361 — don't parse to a number here).
                    const next = e.target.value.replace(/\D/g, "").slice(0, 5);
                    lastEmitted.current = next;
                    setText(next);
                    onChange(next);
                    setOpen(true);
                }}
            />

            {showList &&
                createPortal(
                    <div
                        ref={listRef}
                        style={{
                            position: "fixed",
                            top: rect.bottom + 4,
                            left: rect.left,
                            width: Math.max(rect.width, 240),
                            zIndex: 1000,
                        }}
                        className="ring-secondary_alt max-h-72 overflow-y-auto rounded-lg bg-primary py-1 shadow-lg ring-1"
                    >
                        {loading && results.length === 0 ? (
                            <div className="text-tertiary px-3 py-2 text-sm">Searching…</div>
                        ) : (
                            results.map((z) => (
                                <button
                                    key={z.zipCode}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSelect(z)}
                                    className="hover:bg-primary_hover flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left"
                                >
                                    <span className="text-primary text-sm font-medium">{z.zipCode}</span>
                                    {zipSubline(z) && <span className="text-tertiary text-xs">{zipSubline(z)}</span>}
                                </button>
                            ))
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
}
